import React, { useEffect, useRef, useState } from "react";
import {
  CalendarBody,
  CalendarHeader,
  DateList,
  Day,
  DaysOfWeekWrap,
  Wrap,
  YearMonth,
} from "./styles";
import IconButton from "../IconButton";
import { LeftIcon, RightIcon } from "assets/icons";
import CalendarDateItem from "./CalendarDateItem";
import Divider from "../Divider";
import { isDateBetween, isSameDate, isToday } from "utils/dateUtility";
import CalendarTimer from "./CalendarTimer";
import Select from "../Select/Select";
import { RangeDateType } from "models/DateTypes";

type DatePickerType = "single" | "range";

interface CalendarProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: DatePickerType;
  selectedDate?: Date;
  onChangeDate?: (date: Date) => void;
  isTimePicker?: boolean;
  rangeDate?: RangeDateType;
  onChangeRangeDate?: (rangeDate: RangeDateType) => void;
  onCloseCalendar?: () => void;
}

type DateObj = {
  date: Date;
  disabled?: boolean;
};

const Calendar = ({
  type = "single",
  selectedDate: defaultSelectedDate,
  onChangeDate,
  rangeDate,
  onChangeRangeDate,
  onCloseCalendar,
  isTimePicker = false,
  ...rest
}: CalendarProps) => {
  const today = useRef(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    defaultSelectedDate,
  );

  const [selectedRange, setSelectedRange] = useState<RangeDateType | undefined>(
    rangeDate,
  );

  const [year, setYear] = useState<number>(
    selectedDate?.getFullYear() || today.current.getFullYear(),
  );
  const [month, setMonth] = useState<number>(
    (selectedDate?.getMonth() || today.current.getMonth()) + 1,
  );

  /**
   * 렌더링하기 위한 날짜 정보 객체를 담은 배열
   */
  const [dates, setDates] = useState<DateObj[]>([]);

  /**
   * @description 캘린더의 날짜 버튼을 클릭했을 때의 이벤트 핸들러
   *
   * @param year 클릭된 날짜의 연도
   * @param month 클릭된 날짜의 월
   * @param date 클릭된 날짜의 일
   */
  const handleClickDate = (selectedDate: Date) => {
    setSelectedDate(selectedDate);

    if (type === "single") {
      if (onChangeDate) onChangeDate(selectedDate);
      if (onCloseCalendar && !isTimePicker) onCloseCalendar();
    } else {
      let dateRange: RangeDateType = { start: selectedDate };

      if (selectedRange?.end) {
        dateRange = { start: selectedDate };
      } else if (selectedRange?.start && selectedDate >= selectedRange.start) {
        dateRange = { start: selectedRange.start, end: selectedDate };
      }

      setSelectedRange(dateRange);

      if (onChangeRangeDate) onChangeRangeDate(dateRange);

      // 캘린더의 날짜 범위가 모두 선택되면 닫기
      if (dateRange.start && dateRange.end && onCloseCalendar && !isTimePicker)
        onCloseCalendar();
    }
  };

  /**
   * @description 이전 달로 돌아가는 버튼의 클릭 이벤트 핸들러
   */
  const handlePrevMonth = () => {
    if (month === 1) {
      setYear((prev) => prev - 1);
      setMonth(12);
    } else {
      setMonth((prev) => prev - 1);
    }
  };

  /**
   * @description 다음 달로 넘어가는 버튼의 클릭 이벤트 핸들러
   */
  const handleNextMonth = () => {
    if (month === 12) {
      setYear((prev) => prev + 1);
      setMonth(1);
    } else {
      setMonth((prev) => prev + 1);
    }
  };

  /**
   * @description 연월일 초기화 이펙트
   */
  useEffect(() => {
    const monthIndex = month - 1;

    // UTC 기준으로 Date 객체 생성
    const prevLastDate = new Date(Date.UTC(year, monthIndex, 0));
    const firstDate = new Date(Date.UTC(year, monthIndex, 1));
    const lastDate = new Date(Date.UTC(year, monthIndex + 1, 0));

    // 이전 달 날짜 목록 추가
    const prevMonthDates = getDateObjArray({
      startDate: new Date(
        Date.UTC(year, monthIndex, -firstDate.getUTCDay() + 1),
      ),
      endDate: prevLastDate,
      disabled: true,
    });

    // 이번 달 날짜 목록 추가
    const newDates = getDateObjArray({
      startDate: firstDate,
      endDate: lastDate,
    });

    // 다음 달 날짜 목록 추가
    const nextMonthDates = getDateObjArray({
      startDate: new Date(Date.UTC(year, monthIndex + 1, 1)),
      endDate: new Date(
        Date.UTC(year, monthIndex + 1, 6 - lastDate.getUTCDay()),
      ),
      disabled: true,
    });

    setDates([...prevMonthDates, ...newDates, ...nextMonthDates]);
  }, [year, month]);

  return (
    <Wrap {...rest}>
      <CalendarHeader>
        <IconButton onClick={handlePrevMonth}>
          <LeftIcon />
        </IconButton>

        <YearMonth>
          <Select
            list={[...Array(61).keys()].map((yearOffset) => {
              const year = new Date().getFullYear() - 30 + yearOffset;
              return { label: `${year.toString()}년`, value: year.toString() };
            })}
            value={year.toString()}
            onValueChange={(year) => {
              setYear(Number(year));
            }}
            isCalender
            listViewCount={6}
          />
          <Select
            list={[...Array(12).keys()].map((month) => ({
              label: `${(month + 1).toString().padStart(2, "0")}월`,
              value: (month + 1).toString().padStart(2, "0"),
            }))}
            value={month.toString().padStart(2, "0")}
            onValueChange={(month) => {
              setMonth(Number(month));
            }}
            isCalender
            listViewCount={6}
          />
        </YearMonth>

        <IconButton onClick={handleNextMonth}>
          <RightIcon />
        </IconButton>
      </CalendarHeader>

      <CalendarBody>
        <DaysOfWeek />

        <Divider />

        <DateList>
          {dates.map(({ date, disabled }, index) => {
            const numberDate = date.getDate();
            const isRangeMode = !!onChangeRangeDate;
            const hasSelectedRange = isRangeMode && !!selectedRange;

            const isStartDate =
              hasSelectedRange && isSameDate(date, selectedRange.start);
            const isEndDate =
              hasSelectedRange &&
              selectedRange.end &&
              isSameDate(date, selectedRange.end);
            const isBetween =
              hasSelectedRange &&
              selectedRange.end &&
              isDateBetween(date, selectedRange.start, selectedRange.end);

            const todayFlag =
              !!year &&
              !!month &&
              !!date &&
              isToday({ year, month, date: numberDate });

            const singleSelected =
              !!selectedRange &&
              selectedRange.end &&
              isSameDate(selectedRange.start, selectedRange.end);

            const selected =
              (!!selectedDate && isSameDate(date, selectedDate)) ||
              isStartDate ||
              isEndDate;

            const decorator = () => {
              if (singleSelected && isStartDate) {
                return "all";
              } else if (!singleSelected && isStartDate) {
                return "right";
              } else if (!singleSelected && isEndDate) {
                return "left";
              } else {
                return undefined;
              }
            };

            return (
              <CalendarDateItem
                isBetween={isBetween}
                key={`${year}-${month}-${date}_${index}-${isBetween}`}
                disabled={disabled}
                selected={selected}
                decoratorDirection={decorator()}
                isToday={todayFlag}
                onClick={() => handleClickDate(date)}
              >
                {numberDate}
              </CalendarDateItem>
            );
          })}
        </DateList>
      </CalendarBody>
      {isTimePicker && (
        <>
          <Divider />
          <CalendarTimer
            date={selectedDate}
            onChangeTime={(value) => {
              setSelectedDate(value);
              if (onChangeDate) onChangeDate(value);
            }}
          />
        </>
      )}
    </Wrap>
  );
};

const DaysOfWeek = () => {
  return (
    <DaysOfWeekWrap>
      <Day>일</Day>
      <Day>월</Day>
      <Day>화</Day>
      <Day>수</Day>
      <Day>목</Day>
      <Day>금</Day>
      <Day>토</Day>
    </DaysOfWeekWrap>
  );
};

export default Calendar;

/**
 * @description 캘린더 렌더링에 사용할 날짜 객체 목록을 반환합니다.
 */
function getDateObjArray({
  startDate,
  endDate,
  disabled,
}: {
  startDate: Date;
  endDate: Date;
  disabled?: boolean;
}): DateObj[] {
  const dateObjArray = [];

  if (startDate > endDate) return [];

  for (let i = startDate.getDate(); i <= endDate.getDate(); i++) {
    dateObjArray.push({
      date: new Date(
        Date.UTC(startDate.getFullYear(), startDate.getMonth(), i),
      ),
      disabled,
    });
  }

  return dateObjArray;
}
